import { useState } from "react";
import { fmtMoney } from "@/lib/api";
import NegativesEditor from "@/components/NegativesEditor";

const BudgetEdit = ({ p, editable, onSave }) => {
  const [to, setTo] = useState(p.payload.to);
  return (
    <div className="flex flex-wrap items-center gap-3 font-mono text-sm">
      <span className="text-slate-400">{fmtMoney(p.payload.from)}/day →</span>
      {editable ? <input data-testid={`proposal-budget-input-${p.id}`} type="number" min="1" step="1" className="cc-input w-28" value={to} onChange={(e) => setTo(e.target.value)} />
        : <span className="text-emerald-300">{fmtMoney(p.payload.to)}/day</span>}
      {editable && Number(to) !== p.payload.to && <button data-testid={`proposal-budget-save-${p.id}`} onClick={() => onSave({ to: Number(to) })} className="cc-btn cc-btn-ghost px-3 py-1 text-xs">Save</button>}
    </div>
  );
};

const NegEdit = ({ p, editable, onSave }) => {
  const [items, setItems] = useState(p.payload.items);
  if (!editable) return <div className="flex flex-wrap gap-1.5">{p.payload.items.map((n) => <span key={n.text + n.match_type} className="rounded border border-[#1e293b] px-2 py-0.5 font-mono text-xs text-red-300">−{n.text} · {n.match_type}</span>)}</div>;
  return (
    <div>
      <NegativesEditor items={items} onChange={setItems} testid={`proposal-negatives-${p.id}`} showReason />
      <button data-testid={`proposal-negatives-save-${p.id}`} onClick={() => onSave({ items })} className="cc-btn cc-btn-ghost mt-3 px-3 py-1 text-xs">Save list</button>
    </div>
  );
};

const KeywordList = ({ p }) => (
  <table className="w-full text-left font-mono text-xs"><tbody>{p.payload.items.map((k) => (
    <tr key={k.id + k.ad_group} className="border-b border-[#1e293b] last:border-0"><td className="py-1.5 text-slate-200">{k.text}</td><td className="text-slate-500">{k.match_type}</td><td className="text-slate-500">{k.ad_group}</td><td className="text-right text-amber-300">{fmtMoney(k.cost)} · {k.clicks} clicks · 0 conv</td></tr>
  ))}</tbody></table>
);

const CampaignBudgetEdit = ({ p, editable, onSave }) => {
  const cur = p.payload.campaign.daily_budget;
  const [v, setV] = useState(cur);
  return (
    <div className="mb-3 flex flex-wrap items-center gap-3 font-mono text-sm" data-testid={`proposal-campaign-budget-${p.id}`}>
      <span className="text-slate-400">Daily budget (S$)</span>
      {editable ? <input data-testid={`proposal-campaign-budget-input-${p.id}`} type="number" min="1" step="1" className="cc-input w-28" value={v} onChange={(e) => setV(e.target.value)} />
        : <span className="text-emerald-300">{fmtMoney(cur)}/day</span>}
      {editable && Number(v) !== cur && Number(v) >= 1 && <button data-testid={`proposal-campaign-budget-save-${p.id}`} onClick={() => onSave({ daily_budget: Number(v) })} className="cc-btn cc-btn-ghost px-3 py-1 text-xs">Save</button>}
      <span className="text-xs text-slate-500">≈ {fmtMoney((Number(v) || 0) * 30.4)}/month</span>
    </div>
  );
};

const CampaignSummary = ({ p, editable, onSave }) => (
  <div className="space-y-2 text-xs">
    <CampaignBudgetEdit p={p} editable={editable} onSave={onSave} />
    {p.payload.campaign.ad_groups.map((g, i) => (
      <div key={g.name} className="rounded border border-[#1e293b] p-3">
        <div className="font-semibold text-slate-200">{g.name} <span className="font-normal text-slate-500">· {g.keywords.length} keywords</span></div>
        <div className="mt-1 break-all font-mono text-cyan-300">{p.payload.final_urls_by_group[i]}</div>
        <div className="mt-1 text-slate-500">{(g.responsive_search_ads?.[0]?.headlines || []).slice(0, 4).join(" · ")}</div>
      </div>
    ))}
  </div>
);

const Conversions = ({ p }) => (
  <ul className="space-y-1 text-sm">{p.payload.actions.map((a) => <li key={a.key} className="text-slate-300">{a.name} <span className="font-mono text-xs text-slate-500">{a.type} · {a.category}</span></li>)}</ul>
);

const BudgetShift = ({ p }) => (
  <ul className="space-y-1 font-mono text-sm">{p.payload.items.map((i) => (
    <li key={i.campaign_id} className="text-slate-300">{i.campaign_name}: <span className="text-slate-500">{fmtMoney(i.from)}/day →</span> <span className={i.to < i.from ? "text-amber-300" : "text-emerald-300"}>{fmtMoney(i.to)}/day</span></li>
  ))}</ul>
);

const StatusList = ({ p }) => (
  <ul className="space-y-1 text-sm">{p.payload.items.map((i) => <li key={i.campaign_id} className="text-slate-300">{i.campaign_name} <span className="font-mono text-xs text-slate-500">{i.from} → {i.to} · {fmtMoney(i.daily_budget)}/day</span></li>)}</ul>
);

const AdsList = ({ p }) => (
  <ul className="space-y-1 font-mono text-xs">{p.payload.items.map((a) => <li key={a.ad_id} className="text-slate-300">Ad {a.ad_id} · {a.ad_group} · <span className="text-amber-300">{fmtMoney(a.cost)} · {a.clicks} clicks · 0 conv</span></li>)}</ul>
);

const Rsa = ({ p }) => (
  <div className="rounded border border-[#1e293b] p-3 text-xs">
    <div className="break-all font-mono text-cyan-300">{p.payload.final_url}</div>
    <div className="mt-2 text-slate-200">{p.payload.headlines.join(" · ")}</div>
    <div className="mt-1 text-slate-400">{p.payload.descriptions.join(" · ")}</div>
    {p.payload.replace_ad_id && <div className="mt-1 text-amber-300">Pauses ad {p.payload.replace_ad_id}</div>}
  </div>
);

export const ProposalDetails = ({ p, editable, onSave }) => {
  if (p.kind === "campaign_budget") return <BudgetEdit p={p} editable={editable} onSave={onSave} />;
  if (p.kind === "add_negatives") return <NegEdit p={p} editable={editable} onSave={onSave} />;
  if (p.kind === "pause_keywords") return <KeywordList p={p} />;
  if (p.kind === "create_campaign") return <CampaignSummary p={p} editable={editable} onSave={onSave} />;
  if (p.kind === "conversion_actions") return <Conversions p={p} />;
  if (p.kind === "budget_shift") return <BudgetShift p={p} />;
  if (p.kind === "campaign_status") return <StatusList p={p} />;
  if (p.kind === "pause_ads") return <AdsList p={p} />;
  if (p.kind === "new_rsa") return <Rsa p={p} />;
  if (p.kind === "target_cpa") return <div className="font-mono text-sm text-slate-300">Target CPA {p.payload.from ? fmtMoney(p.payload.from) : "none"} → <span className="text-emerald-300">{fmtMoney(p.payload.to)}</span></div>;
  return null;
};
